<script>
    import { onMount } from "svelte";
    import { Modal, ModalBody, ModalHeader } from "sveltestrap";
    import alertify from "alertifyjs";
    import server from "../../../../server";
    import moment from "moment";
    import Loading from "../../Loading.svelte";
    import {ICONS} from "../../../../js/icons.js";

    export let showFindLogs = false;

    // ===== estado base =====
    let loading = false;
    let groups = {};
    let reqIds = [];
    let current = 0;
    let params = {
        search: "",
        from: "",
        nextTokenJava: "",
        nextTokenNode: ""
    };

    const lvlClass = (lvl) => {
        const v = (lvl || "").toUpperCase();
        return v === "ERROR" ? "lvl-error"
            : v === "WARN"  ? "lvl-warn"
                : v === "DEBUG" ? "lvl-debug"
                    : "lvl-info";
    };
    const highlight = (text, needle) => {
        if (!needle || !text) return text || "";
        const esc = needle.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        return String(text).replace(new RegExp(esc, "gi"), (m) => `<mark>${m}</mark>`);
    };

    const extractJson = (s) => {
        if (!s || typeof s !== "string") return null;
        const start = s.indexOf("{"); const end = s.lastIndexOf("}");
        if (start === -1 || end === -1 || end <= start) return null;
        const raw = s.slice(start, end + 1);
        try { return { json: JSON.parse(raw), before: s.slice(0, start), after: s.slice(end + 1) }; }
        catch { return null; }
    };
    const pretty = (obj) => JSON.stringify(obj, null, 2);

    const copy = async (txt) => {
        try { await navigator.clipboard.writeText(txt); alertify.success("Copiado"); }
        catch { alertify.error("No se pudo copiar"); }
    };

    // Por defecto: hoy
    onMount(() => {
        params.from = moment().format("YYYY-MM-DD");
    });

    // Utilidades de fecha
    const toUtcMidnightString = (ymd) =>
        ymd ? moment.utc(ymd, "YYYY-MM-DD").startOf("day").format("YYYY-MM-DD[T]HH:mm:ss[Z]") : "";

    // === NUEVO: derivar timestamp si no viene directo ===
    const computeTimestamp = (log) => {
        if (log?.timestamp) return String(log.timestamp);
        const parts = extractJson(log?.message);
        const ts = parts?.json?.timestamp || parts?.json?.["@timestamp"];
        return ts ? String(ts) : null;
    };
    const fmtTime = (isoTs) => isoTs ? moment(isoTs).format("HH:mm:ss.SSS") : "";

    // === Llamada backend adaptada al nuevo formato agrupado ===
    const listLogs = async () => {
        try {
            if (!params.search?.trim()) { alertify.error("Ingresa el Transaction ID"); return; }
            if (!params.from) { alertify.error("Selecciona la Fecha de inicio"); return; }

            loading = true;

            const payload = {
                trxSearch: params.search.trim(),
                startDate: toUtcMidnightString(params.from),
                nextTokenJava: params.nextTokenJava || "",
                nextTokenNode: params.nextTokenNode || ""
            };

            const { data } = await server.logs.traceByCodeAndDates(payload);

            // data.logs === { [requestId]: Log[] }
            const rawGroups = data?.logs || {};
            // Ordenamos los requestIds por "mejor timestamp encontrado" asc (o como prefieras)
            const entries = Object.entries(rawGroups).map(([rid, arr]) => {
                // buscamos el primer timestamp disponible
                const firstWithTs = (arr || []).map(l => computeTimestamp(l)).find(Boolean);
                return { rid, ts: firstWithTs ? +new Date(firstWithTs) : Number.MAX_SAFE_INTEGER };
            }).sort((a,b) => a.ts - b.ts);

            reqIds = entries.map(e => e.rid);
            groups = rawGroups;
            current = 0;
        } catch (e) {
            console.error(e);
            alertify.error("Error listando logs");
        } finally {
            loading = false;
        }
    };

    const clearForm = () => {
        params.search = "";
        params.from = moment().format("YYYY-MM-DD");
        params.nextTokenJava = "";
        params.nextTokenNode = "";
        groups = {};
        reqIds = [];
        current = 0;
    };

    const onToggle = () => (showFindLogs = !showFindLogs);

    // ===== Derivados para UI =====
    $: totalReqIds = reqIds.length;
    $: currentReqId = reqIds[current] || "";
    $: currentLogs = currentReqId ? (groups[currentReqId] || []) : [];
    $: firstReqId = reqIds[0] || "";

    // Paginación de Request-ID
    const goto = (i) => { if (i >= 0 && i < totalReqIds) current = i; };
    const prev = () => goto(current - 1);
    const next = () => goto(current + 1);

    // Para select rápido
    const onSelectReqId = (e) => {
        const rid = e?.target?.value || "";
        const idx = reqIds.indexOf(rid);
        if (idx >= 0) current = idx;
    };
</script>

<Modal isOpen={showFindLogs} toggle={onToggle} size={"xl"}>
    <ModalHeader toggle={onToggle}>Buscar TRX</ModalHeader>
    <ModalBody>
        <!-- Filtros -->
        <div class="filter-logs">
            <div>
                <label for="trxid">Transaction ID</label>
                <input id="trxid" class="form-control" bind:value={params.search} placeholder="Buscar por Transaction ID"/>
            </div>

            <div>
                <label for="initDate">Fecha inicio</label>
                <input id="initDate" class="form-control" type="date" bind:value={params.from} />
            </div>

            <div class="btns">
                <button class="btn btn-secondary btn-sm" on:click={clearForm}>Limpiar</button>
                <button class="btn btn-primary btn-sm" on:click={listLogs}>Buscar</button>
            </div>
        </div>

        <!-- Banner principal -->
        {#if totalReqIds}
            <!-- Barra de navegación por Request-ID -->
            <div class="rid-nav">
                <div class="left">
                    <button class="btn btn-light btn-xs" on:click={prev} disabled={current===0}>&laquo; Anterior</button>
                    <span class="idx">({current + 1}/{totalReqIds})</span>
                    <button class="btn btn-light btn-xs" on:click={next} disabled={current===totalReqIds-1}>Siguiente &raquo;</button>
                </div>
            </div>

            <!-- Banner del Request-ID actual -->
            <div class="rid-banner">
                <span>Request-ID:</span>
                <strong>{@html highlight(currentReqId, currentReqId)}</strong>
                <button
                        class="btn btn-light btn-xs copy-btn"
                        on:click={() => copy(currentReqId)}
                        title="Copiar Request-ID"
                        aria-label="Copiar Request-ID"
                        disabled={!currentReqId}
                >
                    {@html ICONS.clipboard}
                </button>
                <span class="muted">•</span>
                <span>{currentLogs.length} logs</span>
            </div>
        {/if}

        <div class="cont-data-log">
            <Loading bind:loading />
            {#if !loading}
                {#if totalReqIds === 0}
                    <p>No se encontraron registros</p>
                {:else}
                    <div class="log-list">
                        {#each currentLogs as log, i (currentReqId + '-' + i)}
                            <article class="log-item">
                                <header class="log-meta">
                                    <span class={"lvl " + lvlClass(log.level)}>{(log.level || "INFO").toUpperCase()}</span>
                                    {#if computeTimestamp(log)}
                                        <span class="chip ts">{fmtTime(computeTimestamp(log))}</span>
                                    {/if}
                                </header>
                                {#if extractJson(log.message)}
                                    {#await Promise.resolve(extractJson(log.message)) then parts}
                                        <div class="msg">
                                            {@html (parts.before || "").trim()}
                                            <details class="json">
                                                <summary>Ver JSON</summary>
                                                <pre><code>{pretty(parts.json)}</code></pre>
                                            </details>
                                            {@html (parts.after || "").trim()}
                                        </div>
                                    {/await}
                                {:else}
                                    <div class="msg">
                                        {@html (log.message || "")}
                                    </div>
                                {/if}
                            </article>
                        {/each}
                    </div>
                {/if}
            {/if}
        </div>
    </ModalBody>
</Modal>

<style>
    .filter-logs {
        display: grid;
        grid-template-columns: 1.2fr 0.8fr auto;
        gap: .5rem;
        align-items: end;
        margin-bottom: .5rem;
    }

    .btns {
        display: flex;
        gap: .5rem;
        justify-content: flex-end;
    }

    .result-banner {
        display: flex;
        gap: .5rem;
        align-items: center;
        padding: .5rem .75rem;
        border-radius: .5rem;
        margin: .25rem 0 .5rem;
        background: #f8fafc;
        border: 1px solid #e5e7eb;
        font-size: .92rem;
    }

    .rid-nav {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: .75rem;
        margin: .5rem 0;
    }

    .rid-nav .left {
        display: flex;
        gap: .5rem;
        align-items: center;
    }

    .rid-select {
        min-width: 420px;
    }

    .sel-label {
        font-size: .85rem;
        color: #555;
    }

    .idx {
        font-size: .85rem;
        color: #444;
    }

    .rid-banner {
        display: flex;
        gap: .5rem;
        align-items: center;
        padding: .4rem .6rem;
        border-radius: .5rem;
        margin: .25rem 0 .5rem;
        background: #f6f7fb;
        border: 1px dashed #d6daf1;
        font-size: .9rem;
        word-break: break-all;
    }

    .cont-data-log {
        width: 100%;
        background-color: #fff;
        border-radius: 0.5rem;
        border: 1px solid #000;
        padding: 1rem;
        overflow: auto;
        min-height: 3.5rem;
        max-height: 65vh;
        position: relative;
    }

    .log-list {
        display: flex;
        flex-direction: column;
        gap: .5rem;
    }

    .log-item {
        border: 1px solid #e3e3e3;
        border-radius: .5rem;
        padding: .5rem .75rem;
        background: #fff;
    }

    .log-meta {
        display: flex;
        flex-wrap: wrap;
        gap: .5rem .6rem;
        align-items: center;
        font-size: .82rem;
        margin-bottom: .4rem;
    }

    .ts {
        font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
        color: #444;
    }

    .lvl {
        padding: .05rem .4rem;
        border-radius: .75rem;
        font-weight: 700;
        letter-spacing: .02em;
        font-size: .72rem;
    }

    .lvl-info {
        background: #e8f1ff;
        color: #1158cc;
    }

    .lvl-debug {
        background: #eef5ef;
        color: #2f7d32;
    }

    .lvl-warn {
        background: #fff5e6;
        color: #a66300;
    }

    .lvl-error {
        background: #ffecec;
        color: #c62828;
    }

    .chip {
        border: 1px solid #e6e6e6;
        background: #f9f9f9;
        border-radius: .35rem;
        padding: .05rem .35rem;
        font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    }

    .msg {
        white-space: pre-wrap;
        word-break: break-word;
        font-size: .9rem;
    }

    .json summary {
        cursor: pointer;
        user-select: none;
        margin: .25rem 0;
    }

    .json pre {
        margin: .35rem 0 0;
        padding: .5rem;
        background: #0f172a;
        color: #e2e8f0;
        border-radius: .35rem;
        overflow: auto;
        max-height: 40vh;
    }

    .btn-xs {
        padding: .2rem .5rem;
        font-size: .75rem;
        border-radius: .35rem;
    }

    .btn-light.btn-xs {
        border: 1px solid #ddd;
    }

    @media (max-width: 1024px) {
        .filter-logs {
            grid-template-columns: 1fr;
        }

        .btns {
            justify-content: stretch;
        }

        .rid-select {
            min-width: 240px;
        }
    }
    .copy-btn {
        margin-left: .15rem;
    }
</style>
